
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trophy, Calendar, Star, Menu, X } from 'lucide-react';

const Results = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [countdown, setCountdown] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Mock results data
  const results = [
    {
      id: 1,
      date: '2025-01-15',
      numbers: [7, 14, 23, 31, 42, 45],
      bonusNumber: 12,
      prize: '$2,500,000',
      winners: 3,
      month: '2025-01'
    },
    {
      id: 2,
      date: '2025-01-08',
      numbers: [3, 18, 26, 33, 39, 44],
      bonusNumber: 8,
      prize: '$1,800,000',
      winners: 2,
      month: '2025-01'
    },
    {
      id: 3,
      date: '2025-01-01',
      numbers: [9, 15, 22, 28, 35, 41],
      bonusNumber: 17,
      prize: '$3,200,000',
      winners: 1,
      month: '2025-01'
    },
    {
      id: 4,
      date: '2024-12-25',
      numbers: [1, 11, 19, 27, 36, 43],
      bonusNumber: 24,
      prize: '$5,000,000',
      winners: 2,
      month: '2024-12'
    },
    {
      id: 5,
      date: '2024-12-18',
      numbers: [5, 13, 21, 29, 37, 46],
      bonusNumber: 16,
      prize: '$1,500,000',
      winners: 4,
      month: '2024-12'
    }
  ];

  const filteredResults = selectedMonth === 'all' 
    ? results 
    : results.filter(result => result.month === selectedMonth);

  // Countdown to next draw (every Tuesday at 8 PM AEST)
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextDraw = new Date();
      
      // Set to next Tuesday at 8 PM
      const dayOfWeek = now.getDay();
      const daysUntilTuesday = (2 - dayOfWeek + 7) % 7 || 7;
      
      nextDraw.setDate(now.getDate() + daysUntilTuesday);
      nextDraw.setHours(20, 0, 0, 0);
      
      // If it's Tuesday and past 8 PM, move to next Tuesday
      if (dayOfWeek === 2 && now.getHours() >= 20) {
        nextDraw.setDate(nextDraw.getDate() + 7);
      }
      
      const timeDiff = nextDraw.getTime() - now.getTime();
      
      if (timeDiff > 0) {
        const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((timeDiff % (1000 * 60)) / 1000);
        
        setCountdown({ days, hours, minutes, seconds });
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-30">
        <nav className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-gradient-to-r from-yellow-500 to-yellow-300 rounded transform rotate-12"></div>
              <h1 className="text-2xl font-bold text-yellow-600">GoldenTicketLucky</h1>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-6">
              <a href="/" className="text-gray-700 hover:text-yellow-600 transition-colors">Home</a>
              <a href="/about" className="text-gray-700 hover:text-yellow-600 transition-colors">About</a>
              <a href="/results" className="text-yellow-600 font-medium">Results</a>
              
              <a href="/contact" className="text-gray-700 hover:text-yellow-600 transition-colors">Contact</a>
              <div className="relative group">
                <button className="text-gray-700 hover:text-yellow-600 transition-colors">Legal</button>
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                  <a href="/terms" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Terms</a>
                  <a href="/privacy" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Privacy</a>
                  <a href="/cookies" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Cookies</a>
                  <a href="/accessibility" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Accessibility</a>
                  <a href="/notices" className="block px-4 py-2 text-gray-700 hover:bg-yellow-50">Notices</a>
                </div>
              </div>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <div className="md:hidden mt-4 space-y-2">
              <a href="/" className="block py-2 text-gray-700 hover:text-yellow-600">Home</a>
              <a href="/about" className="block py-2 text-gray-700 hover:text-yellow-600">About</a>
              <a href="/results" className="block py-2 text-yellow-600 font-medium">Results</a>
              <a href="/updates" className="block py-2 text-gray-700 hover:text-yellow-600">Updates</a>
              <a href="/contact" className="block py-2 text-gray-700 hover:text-yellow-600">Contact</a>
              <a href="/terms" className="block py-2 text-gray-700 hover:text-yellow-600">Terms</a>
              <a href="/privacy" className="block py-2 text-gray-700 hover:text-yellow-600">Privacy</a>
            </div>
          )}
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-yellow-500 to-yellow-300 py-16">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Draw Results
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Check the latest winning numbers and see if you're our next big winner!
          </p>
        </div>
      </section>

      {/* Next Draw Countdown */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">Next Draw Countdown</h2>
          <p className="text-lg text-gray-700 mb-8">Every Tuesday at 8:00 PM AEST</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto mb-8">
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-yellow-600">{countdown.days}</div>
                <div className="text-gray-700">Days</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-yellow-600">{countdown.hours}</div>
                <div className="text-gray-700">Hours</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-yellow-600">{countdown.minutes}</div>
                <div className="text-gray-700">Minutes</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-6 text-center">
                <div className="text-4xl font-bold text-yellow-600">{countdown.seconds}</div>
                <div className="text-gray-700">Seconds</div>
              </CardContent>
            </Card>
          </div>
          
          <Button 
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 text-lg rounded-md"
            onClick={() => window.location.href = '/'}
          >
            Join the Next Draw
          </Button>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">Recent Results</h2>
            
            <div className="flex items-center space-x-4">
              <label htmlFor="month-filter" className="text-gray-700 font-medium">Filter by month:</label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Select month" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Results</SelectItem>
                  <SelectItem value="2025-01">January 2025</SelectItem>
                  <SelectItem value="2024-12">December 2024</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-6">
            {filteredResults.map((result) => (
              <Card key={result.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-8">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
                    {/* Date and Prize */}
                    <div className="text-center lg:text-left">
                      <div className="flex items-center justify-center lg:justify-start mb-2">
                        <Calendar className="w-5 h-5 text-yellow-600 mr-2" />
                        <span className="text-lg font-medium text-gray-900">
                          {new Date(result.date).toLocaleDateString('en-AU', {
                            weekday: 'long',
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="text-3xl font-bold text-yellow-600">{result.prize}</div>
                      <div className="text-gray-700">{result.winners} winner{result.winners !== 1 ? 's' : ''}</div>
                    </div>

                    {/* Winning Numbers */}
                    <div className="text-center">
                      <h3 className="text-lg font-bold text-gray-900 mb-4">Winning Numbers</h3>
                      <div className="flex justify-center space-x-2 mb-2">
                        {result.numbers.map((number, index) => (
                          <div
                            key={index}
                            className="w-12 h-12 bg-yellow-600 text-white rounded-full flex items-center justify-center font-bold text-lg"
                          >
                            {number}
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-center items-center space-x-2">
                        <span className="text-gray-700">Bonus:</span>
                        <div className="w-10 h-10 bg-gray-600 text-white rounded-full flex items-center justify-center font-bold">
                          {result.bonusNumber}
                        </div>
                      </div>
                    </div>

                    {/* Trophy Icon */}
                    <div className="text-center lg:text-right">
                      <Trophy className="w-16 h-16 text-yellow-600 mx-auto lg:ml-auto lg:mr-0" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics Section */}
      <section className="py-16 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">Draw Statistics</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="p-8">
                <Star className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Most Drawn Number</h3>
                <div className="text-4xl font-bold text-yellow-600">23</div>
                <p className="text-gray-700">Drawn 47 times this year</p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="p-8">
                <Trophy className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Biggest Jackpot</h3>
                <div className="text-4xl font-bold text-yellow-600">$5M</div>
                <p className="text-gray-700">Christmas Day 2024</p>
              </CardContent>
            </Card>
            
            <Card className="text-center">
              <CardContent className="p-8">
                <Calendar className="w-12 h-12 text-yellow-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Total Winners</h3>
                <div className="text-4xl font-bold text-yellow-600">12</div>
                <p className="text-gray-700">In the last 5 draws</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-300">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Could You Be Next?</h2>
          <p className="text-xl text-gray-700 mb-8">Join thousands of Australians in the next GoldenTicketLucky draw!</p>
          <Button 
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-8 py-4 text-lg rounded-md transition-all transform hover:scale-105"
            onClick={() => window.location.href = '/'}
          >
            Play Now
          </Button>
        </div>
      </section>

      {/* Responsible Gambling Notice */}
      <section className="py-8 bg-gray-100">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-gray-700 mb-4">
            <strong>Play Responsibly.</strong> Gambling should be fun and entertaining. 
            If you're concerned about your gambling, help is available.
          </p>
          <div className="space-x-4">
            <a 
              href="https://gambleaware.nsw.gov.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-600 underline hover:text-yellow-700"
            >
              GambleAware NSW
            </a>
            <a 
              href="https://betstop.gov.au" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-600 underline hover:text-yellow-700"
            >
              BetStop National Self-Exclusion
            </a>
          </div>
          <p className="text-gray-600 text-sm mt-2">Call 1800 858 858 for gambling help</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold text-yellow-500 mb-4">GoldenTicketLucky</h3>
              <p className="text-gray-300 text-sm">
                Dream big, play responsibly. Your trusted Australian lottery experience.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="/" className="block text-gray-300 hover:text-yellow-500">Home</a>
                <a href="/about" className="block text-gray-300 hover:text-yellow-500">About Us</a>
                <a href="/results" className="block text-gray-300 hover:text-yellow-500">Draw Results</a>
                <a href="/contact" className="block text-gray-300 hover:text-yellow-500">Contact</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Legal</h4>
              <div className="space-y-2 text-sm">
                <a href="/terms" className="block text-gray-300 hover:text-yellow-500">Terms & Conditions</a>
                <a href="/privacy" className="block text-gray-300 hover:text-yellow-500">Privacy Policy</a>
                <a href="/cookies" className="block text-gray-300 hover:text-yellow-500">Cookie Policy</a>
                <a href="/accessibility" className="block text-gray-300 hover:text-yellow-500">Accessibility</a>
              </div>
            </div>
            
            <div>
              <h4 className="font-bold mb-4">Get Help</h4>
              <div className="space-y-2 text-sm">
                <a href="https://gambleaware.nsw.gov.au" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-yellow-500">GambleAware</a>
                <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="block text-gray-300 hover:text-yellow-500">BetStop</a>
                <p className="text-gray-300">+61 35 9294 105</p>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-300">
            <p>© 2025 GoldenTicketLucky. All rights reserved. | Licensed under NSW Lotteries Act | 18+ only</p>
            <p className="mt-2">
              BetStop — National Self-Exclusion Register. A free tool to opt out of online wagering. Visit{' '}
              <a href="https://betstop.gov.au" target="_blank" rel="noopener noreferrer" className="text-yellow-500 underline">
                betstop.gov.au
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Results;
